<script setup>
// Reference 6 — the honesty contract, and the part of this page that sells hardest.
//
// A manager who picks software for other people's money checks the boundary first,
// and a page that only lists strengths reads as a page that hides something. So this
// is designed as a deliberate instrument rather than an apology list: a two-column
// ledger, WEL against NIET, split by a hard ink rule. The three on the left are the
// three the lead already names; the six on the right are the shipped boundary,
// verbatim.
//
// Keep the NIET list in sync with what actually ships. It is load-bearing copy, and
// VoorVveBeheerders.spec.js holds it to account.
import { ref, onMounted, onUnmounted } from "vue";
import { Check, X } from "@lucide/vue";
import { useMotion, reveal } from "@/lib/motion";

const doing = ["De terugkerende bijdrage", "De machtiging", "De betaalstatus"];

const notDoing = [
  "Geen boekhouding, begroting of jaarrekening, geen MJOP, geen ALV-ondersteuning en geen technisch beheer.",
  "Geen export naar of koppeling met een boekhoudpakket, en geen bulkexport of auditfile.",
  "Geen bulkacties over VvE's heen en geen portefeuilleoverzicht op één scherm: je werkt per VvE.",
  "Geen reeks herhaalpogingen: één extra poging per mislukte afschrijving, automatisch na een paar werkdagen of eerder op jouw klik — daarna nooit opnieuw op dezelfde rekening.",
  "Geen herinneringsreeks, aanmaningen, rente of achterstandsaldo, en geen formele incasso van een achterstand — het blijft bij die ene melding aan de eigenaar. Verder contact leg jij of het bestuur.",
  "Geen bericht aan eigenaren over een geslaagde betaling, een aflopende machtiging of een aankomende run, en geen sms of pushberichten.",
];

const root = ref(null);
const columns = ref([]);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    reveal(columns.value, { stagger: 0.1, y: 16, trigger: root.value, start: "top 80%" });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <section id="grens" ref="root" class="border-t-2 border-foreground bg-card">
    <div class="mx-auto w-full max-w-6xl px-5 py-10 sm:py-16">
      <h2 class="max-w-3xl font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">Wat Pandig niet doet</h2>
      <p class="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:mt-4 sm:text-base">
        Dit is waarschijnlijk het nuttigste stuk van deze pagina. Pandig doet één deel van het financieel beheer: de terugkerende bijdrage, de
        machtiging en de betaalstatus. De rest niet.
      </p>

      <!-- One border, drawn once: the ledger's own vertical rule is the left border of
           the NIET column from lg: up, where the two sit side by side. -->
      <div class="mt-6 grid border-2 border-foreground bg-background shadow-[var(--shadow-hard)] sm:mt-10 lg:grid-cols-[18rem_1fr]">
        <div :ref="(el) => (columns[0] = el)" class="border-b-2 border-foreground p-4 sm:p-6 lg:border-b-0">
          <h3 class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-success sm:text-[0.6875rem]">Wel</h3>
          <ul class="mt-3 grid gap-2 sm:mt-4 sm:gap-3">
            <li v-for="item in doing" :key="item" class="flex items-start gap-2.5">
              <span class="mt-0.5 flex size-5 shrink-0 items-center justify-center border-2 border-foreground bg-secondary text-success">
                <Check class="size-3" />
              </span>
              <span class="font-sans text-sm font-semibold leading-snug text-foreground sm:text-base">{{ item }}</span>
            </li>
          </ul>
        </div>

        <div :ref="(el) => (columns[1] = el)" class="p-4 sm:p-6 lg:border-l-2 lg:border-foreground">
          <h3 class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground sm:text-[0.6875rem]">Niet</h3>
          <ul class="mt-3 grid gap-2.5 sm:mt-4 sm:gap-3.5">
            <li v-for="item in notDoing" :key="item" class="flex items-start gap-2.5">
              <span class="mt-0.5 flex size-5 shrink-0 items-center justify-center border-2 border-foreground/40 text-muted-foreground">
                <X class="size-3" />
              </span>
              <span class="text-sm leading-relaxed text-muted-foreground sm:text-base">{{ item }}</span>
            </li>
          </ul>
        </div>
      </div>

      <p class="mt-5 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:mt-6 sm:text-base">
        Wat je bij een achterstand zelf mag doen en waar de wettelijke grens ligt, staat op
        <RouterLink to="/vve-betalingsachterstand" class="font-semibold text-primary underline-offset-4 hover:underline"
          >betalingsachterstand in de VvE</RouterLink
        >.
      </p>
    </div>
  </section>
</template>
